<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import Spinner from 'shared/components/Spinner.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const isFetching = ref(false);
const isAssigning = ref(false);
const aiAgents = ref([]);
const currentAgent = ref(null);

const currentChat = computed(() => store.getters.getSelectedChat);

// Format agents for dropdown
const formattedAgents = computed(() => {
  const noneOption = {
    id: null,
    name: t('CONVERSATION.AI_AGENTS.NONE'),
    description: '',
  };

  return [
    noneOption,
    ...aiAgents.value.map(agent => ({
      ...agent,
      thumbnail: agent.avatar_url,
    })),
  ];
});

// Get selected item for dropdown
const selectedAgent = computed(() => {
  if (!currentAgent.value) {
    return formattedAgents.value[0]; // "Nenhum"
  }
  return (
    formattedAgents.value.find(a => a.id === currentAgent.value.id) ||
    formattedAgents.value[0]
  );
});

const fetchAiAgents = async () => {
  if (!props.conversationId) return;

  isFetching.value = true;
  try {
    const response = await ConversationApi.getAiAgents(props.conversationId);
    aiAgents.value = response.data.assistants || [];
    currentAgent.value = response.data.current_assistant || null;
  } catch (error) {
    console.error('[AiAgentsSelector] Failed to fetch agents:', error);
    aiAgents.value = [];
  } finally {
    isFetching.value = false;
  }
};

const onSelectAgent = async agent => {
  if (isAssigning.value) return;

  // Se clicar no mesmo agente selecionado, não faz nada
  if (agent.id === currentAgent.value?.id) return;

  isAssigning.value = true;
  const previousAgent = currentAgent.value;

  // Optimistic update
  currentAgent.value = agent.id ? agent : null;

  try {
    await ConversationApi.assignAiAgent({
      conversationId: props.conversationId,
      agent: agent.id ? agent : null,
    });

    // Update additional_attributes in store
    const additionalAttributes = {
      ...(currentChat.value?.additional_attributes || {}),
      ai_assistant: agent.id ? agent : null,
    };
    store.commit('UPDATE_CONVERSATION_ADDITIONAL_ATTRIBUTES', {
      conversationId: props.conversationId,
      additionalAttributes,
    });

    useAlert(
      agent.id
        ? t('CONVERSATION.AI_AGENTS.ASSIGNED', { name: agent.name })
        : t('CONVERSATION.AI_AGENTS.REMOVED')
    );
  } catch (error) {
    // Rollback
    currentAgent.value = previousAgent;
    console.error('[AiAgentsSelector] Failed to assign agent:', error);
    useAlert(t('CONVERSATION.AI_AGENTS.ERROR'));
  } finally {
    isAssigning.value = false;
  }
};

// Watch for conversation changes
watch(
  () => props.conversationId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      fetchAiAgents();
    }
  }
);

// Sync with store when conversation updates externally
watch(
  () => currentChat.value?.additional_attributes?.ai_assistant,
  newAgent => {
    // Compare by ID to avoid reference comparison issues
    const newId = newAgent?.id ?? null;
    const currentId = currentAgent.value?.id ?? null;
    if (newId !== currentId) {
      currentAgent.value = newAgent || null;
    }
  }
);

onMounted(() => {
  fetchAiAgents();
});
</script>

<template>
  <div class="ai-agents-selector">
    <div v-if="isFetching" class="flex items-center justify-center py-2">
      <Spinner size="small" />
    </div>
    <MultiselectDropdown
      v-else
      :options="formattedAgents"
      :selected-item="selectedAgent"
      :multiselector-title="$t('CONVERSATION.AI_AGENTS.TITLE')"
      :multiselector-placeholder="$t('CONVERSATION.AI_AGENTS.PLACEHOLDER')"
      :no-search-result="$t('CONVERSATION.AI_AGENTS.NO_RESULT')"
      :is-loading="isAssigning"
      @select="onSelectAgent"
    />
  </div>
</template>
